/**
 * src/services/geoLinkTradeService.ts
 *
 * Typed client for GeoLink's pending Zenex Trade pipeline. Mirrors the
 * shape of `geoLinkDepositService.ts` but targets the
 * `/api/contracts/rules/pending/trades/{open|close|:id|:id/complete|:id/cancel}`
 * endpoints documented in the integration guide.
 *
 * Routing: all calls go through the GeoTrust backend's generic GeoLink
 * proxy (`/api/v2/geolink/proxy/*`) so the browser never sees CORS errors
 * and the Wallet Provider API key stays server-side.
 *
 * Dedup guidance: the returned `id` (e.g. `trade_open_234301_22_GDPMU…`)
 * changes on every location update until the user acts. Consumers MUST
 * deduplicate by the server-populated `logical_key`
 * (`${rule_id}_${matched_public_key}`), which is stable across
 * `id`-churn and dedup-resurrections — see §4.1 / §6 of
 * `docs/geolink-zenex-trade-pending-actions.md`.
 *
 * Server vocabulary:
 *   status      = 'pending' | 'completed' | 'cancelled' | 'not_pending' | 'expired'
 *   cancel.reason = 'user_declined' | 'expired' | 'insufficient_balance'
 *                 | 'price_feed_unavailable' | 'other' (nullable)
 *
 * `failed` is NOT part of the server vocabulary. On-chain reverts are
 * reported back via POST /cancel and stored as `cancelled` with a
 * non-null `reason`.
 */

// ── Base URL ──────────────────────────────────────────────────────────────────
// In the browser we route through the GeoTrust backend proxy (same rationale
// as `geolinkApi.ts`); in a node context (SSR / tests) we go direct.
const GEOLINK_TRADE_API_BASE =
  typeof window !== 'undefined'
    ? '/api/v2/geolink/proxy'
    : (process.env.REACT_APP_GEOLINK_API_URL || 'https://testnet.stellargeolink.com');

// ── Shared types ──────────────────────────────────────────────────────────────

export type PendingTradeKind = 'open' | 'close';

/**
 * Unified status vocabulary across the list endpoints, POST /cancel,
 * POST /complete, and GET /:action_id.
 *
 * The list endpoints (`/open` + `/close`) only emit rows with
 * `status === 'pending'`; terminal values (`completed`, `cancelled`,
 * `not_pending`, `expired`) surface on `/cancel` responses and the
 * per-id fetch. We type the union broadly so a single interface models
 * both list entries and detail responses.
 */
export type TradeActionStatus =
  | 'pending'
  | 'completed'
  | 'cancelled'
  | 'not_pending'
  | 'expired';

/** @deprecated Kept as an alias for back-compat; prefer `TradeActionStatus`. */
export type PendingTradeStatus = TradeActionStatus;

export interface PendingTradeLocation {
  latitude: number;
  longitude: number;
}

export interface PendingTradePriceHint {
  market_id: number;
  symbol: string;
  feed_id: number;
  price_decimals: number;
  price_source: string;
}

/**
 * One pending action as returned by
 * `GET /api/contracts/rules/pending/trades/{open|close}`.
 *
 * `parameters` is kept as `Record<string, string>` because GeoLink echoes
 * deterministic rule inputs (market_id, collateral, notional_size, is_long,
 * take_profit, stop_loss, price, user) as strings. `price` typically carries
 * the placeholder `"[GeoTrust must fetch from Pyth price feed and pass as
 * Bytes]"` — consumers replace it with the hex bytes fetched from the
 * server's price proxy.
 */
export interface PendingTradeAction {
  id: string;
  kind: PendingTradeKind;
  /**
   * Stable dedup key that doesn't churn when GeoLink rotates `id` on
   * every location update. Format: `${rule_id}_${matched_public_key}`
   * with the full public key (not the truncated 8-char slug in `id`).
   * Server-populated since commit c55be5b; use `tradeLogicalKey()`
   * rather than reading it directly so stubs / fixtures without the
   * field still work.
   */
  logical_key: string;
  rule_id: number;
  rule_name?: string;
  contract_id?: number;
  contract_name?: string;
  contract_address: string;
  network: 'testnet' | 'mainnet' | string;
  function_name: 'open_market' | 'close_position' | string;
  matched_public_key: string;
  update_id?: number;
  received_at: string;
  expires_at?: string;
  status: TradeActionStatus;
  parameters: Record<string, string | number | boolean>;
  price_hint: PendingTradePriceHint | null;
  location?: PendingTradeLocation | null;
}

export interface ListPendingTradesResponse {
  success: boolean;
  count?: number;
  total?: number;
  pending_trades?: PendingTradeAction[];
  /** Backward-compat: some builds might return `trades` — we collapse to pending_trades. */
  trades?: PendingTradeAction[];
  error?: string;
}

export interface CompleteTradeBody {
  public_key: string;
  transaction_hash: string;
  ledger?: number | null;
  /** Required for `open_market` — the u32 return value. */
  position_id?: number | null;
  /** Optional echo so GeoLink's audit feed can group by market. */
  market_id?: number | null;
}

export interface CompleteTradeResponse {
  success: boolean;
  stellar_expert_url?: string;
  error?: string;
}

/**
 * Cancel-reason enum shared between the request (what the user declared)
 * and the terminal server echo (what was stored on the original cancel
 * — NOT the reason on a re-send). `null` is possible on the echo side
 * for legacy rows with no stored reason.
 */
export type CancelReason =
  | 'user_declined'
  | 'expired'
  | 'insufficient_balance'
  | 'price_feed_unavailable'
  | 'other';

export interface CancelTradeBody {
  public_key: string;
  reason: CancelReason;
}

/**
 * Condensed view of a trade action embedded in /cancel + /:id responses.
 * Always carries `id`, `kind`, `status`; terminal rows additionally carry
 * `cancelled_at` / `reason` (cancelled) or `transaction_hash`
 * (completed).
 */
export interface CancelTradeActionSummary {
  id: string;
  kind: PendingTradeKind;
  status: TradeActionStatus;
  cancelled_at?: string;
  reason?: CancelReason | null;
  transaction_hash?: string;
  completed_at?: string;
}

/**
 * Response shape for POST /api/contracts/rules/pending/trades/:id/cancel.
 *
 * Two distinct flat shapes share this union:
 *
 * 1. Fresh cancel (pending → cancelled) — 200:
 *      { success: true, message, trade_action: {
 *          id, kind, status: 'cancelled', cancelled_at, reason } }
 *    — no top-level `status`, no `already_terminal`.
 *
 * 2. Idempotent terminal (cancelled / completed / not_pending) — 200:
 *      { success: true, already_terminal: true, status, message,
 *        trade_action: { ... } }
 *
 * 404 and 500 are thrown as Error via `request()`.
 */
export interface CancelTradeResponse {
  success: true;
  /** Present ONLY on the idempotent path. */
  already_terminal?: boolean;
  /** Present ONLY on the idempotent path. */
  status?: TradeActionStatus;
  message?: string;
  trade_action: CancelTradeActionSummary;
  /** Legacy field tolerated for back-compat with pre-c55be5b builds. */
  error?: string;
}

/**
 * Response shape for GET /api/contracts/rules/pending/trades/:action_id.
 * Superset of the list entry plus terminal-state fields. Wrapped the
 * same way as /cancel so callers can share a single decoder.
 */
export interface PendingTradeDetail extends PendingTradeAction {
  status: TradeActionStatus;
  /**
   * End of the (rule_id, matched_public_key) cancel-suppression window,
   * ISO-8601 UTC. Clipped to `null` once the window lapses. Always
   * `null` for `close_position` since its cancel-suppression is bypassed
   * (see integration-guide §4.4 / §4.5).
   */
  suppressed_until: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: CancelReason | null;
  transaction_hash?: string | null;
  position_id?: number | null;
  market_id?: number | null;
}

export interface GetPendingTradeByIdResponse {
  success: true;
  trade_action: PendingTradeDetail;
}

// ── Internals ─────────────────────────────────────────────────────────────────

async function request<T>(
  path: string,
  init: RequestInit = {},
  suppressErrorLog = false,
): Promise<T> {
  const url = `${GEOLINK_TRADE_API_BASE}${path}`;
  // No auth header: the proxy injects the server-side key and discards
  // anything we send. A node caller needing direct access passes its own
  // `X-API-Key` through `init.headers`.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };

  let resp: Response;
  try {
    resp = await fetch(url, { ...init, headers });
  } catch (err) {
    if (!suppressErrorLog) {
    }
    throw err;
  }

  const contentType = resp.headers.get('content-type') || '';
  const parsed = contentType.includes('application/json')
    ? await resp.json().catch(() => ({}))
    : await resp.text();

  if (!resp.ok) {
    const msg =
      typeof parsed === 'object' && parsed && (parsed as { error?: string }).error
        ? (parsed as { error: string }).error
        : `HTTP ${resp.status}: ${resp.statusText}`;
    const err = new Error(msg);
    (err as { status?: number }).status = resp.status;
    throw err;
  }
  return parsed as T;
}

function normalizeListResponse(raw: ListPendingTradesResponse): PendingTradeAction[] {
  if (!raw) return [];
  if (Array.isArray(raw.pending_trades)) return raw.pending_trades;
  if (Array.isArray(raw.trades)) return raw.trades;
  return [];
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * List pending `open_market` actions for a user.
 * The GeoLink server already dedupes on `(rule_id, matched_public_key)` and
 * suppresses actions whose `expires_at` has passed.
 */
export async function getPendingOpens(
  publicKey: string,
): Promise<PendingTradeAction[]> {
  if (!publicKey) return [];
  const qs = `?public_key=${encodeURIComponent(publicKey)}`;
  try {
    const raw = await request<ListPendingTradesResponse>(
      `/api/contracts/rules/pending/trades/open${qs}`,
    );
    return normalizeListResponse(raw);
  } catch (err) {
    const status = (err as { status?: number })?.status;
    // 404 / 501 = endpoint not yet deployed; treat as empty list so the UI
    // stays silent until GeoLink rolls it out.
    if (status === 404 || status === 501) return [];
    throw err;
  }
}

/**
 * List pending `close_position` actions for a user.
 * `price_hint` is typically null on this list — GeoTrust is expected to
 * resolve the market from the open position on-chain (see §5.2 of the
 * integration guide / `/api/v2/zenex/lookup-open-position`).
 */
export async function getPendingCloses(
  publicKey: string,
): Promise<PendingTradeAction[]> {
  if (!publicKey) return [];
  const qs = `?public_key=${encodeURIComponent(publicKey)}`;
  try {
    const raw = await request<ListPendingTradesResponse>(
      `/api/contracts/rules/pending/trades/close${qs}`,
    );
    return normalizeListResponse(raw);
  } catch (err) {
    const status = (err as { status?: number })?.status;
    if (status === 404 || status === 501) return [];
    throw err;
  }
}

/**
 * Report completion to GeoLink after the Soroban tx is mined.
 *
 * IMPORTANT: for `trade_open_*` entries, pass `position_id` (the `u32` return
 * value from `open_market`). You don't strictly need it to satisfy GeoLink,
 * but storing it lets the audit feed group open/close pairs. GeoTrust should
 * also cache it locally — §5.2 of the integration guide relies on an
 * on-chain lookup to resolve it again if the client state is lost.
 */
export async function completeTrade(
  actionId: string,
  body: CompleteTradeBody,
): Promise<CompleteTradeResponse> {
  if (!actionId) throw new Error('actionId is required');
  if (!body?.public_key) throw new Error('public_key is required');
  if (!body?.transaction_hash) throw new Error('transaction_hash is required');
  return request<CompleteTradeResponse>(
    `/api/contracts/rules/pending/trades/${encodeURIComponent(actionId)}/complete`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/**
 * Cancel a pending trade.
 *
 * Response contract (commit c55be5b):
 *   - 200 fresh success      → `{ success: true, trade_action: { ... } }`
 *   - 200 already-terminal   → `{ success: true, already_terminal: true,
 *                                 status, trade_action: { ... } }`
 *   - 404                    → thrown with `status: 404`
 *   - 500                    → thrown with `status: 500` (real fault)
 *
 * GeoLink applies a 24h `(rule_id, matched_public_key)` cancel-
 * suppression window on `open_market` cancels. `close_position` is
 * bypassed from that window (§4.5 of the integration guide).
 */
export async function cancelTrade(
  actionId: string,
  body: CancelTradeBody,
): Promise<CancelTradeResponse> {
  if (!actionId) throw new Error('actionId is required');
  if (!body?.public_key) throw new Error('public_key is required');
  if (!body?.reason) throw new Error('reason is required');
  return request<CancelTradeResponse>(
    `/api/contracts/rules/pending/trades/${encodeURIComponent(actionId)}/cancel`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/**
 * Fetch the current state of a single pending trade action by its id.
 * Returns `null` when the action doesn't exist for this
 * `(action_id, public_key)` pair — including the IDOR case where the
 * authenticated session is for a different wallet (server returns
 * 404, never the payload, to avoid leaking existence).
 *
 * Use for:
 *   - Self-heal when we suspect a poll snapshot is stale
 *     (e.g. out-of-band resolution on zenex.trade).
 *   - Rendering `suppressed_until` in on-expand detail views.
 *   - Post-deploy integration tests against the 5-row /cancel matrix.
 */
export async function getPendingTradeById(
  actionId: string,
  publicKey: string,
): Promise<PendingTradeDetail | null> {
  if (!actionId || !publicKey) return null;
  const qs = `?public_key=${encodeURIComponent(publicKey)}`;
  try {
    const raw = await request<GetPendingTradeByIdResponse>(
      `/api/contracts/rules/pending/trades/${encodeURIComponent(actionId)}${qs}`,
      {},
      /* suppressErrorLog */ true,
    );
    return raw?.trade_action ?? null;
  } catch (err) {
    const status = (err as { status?: number })?.status;
    // 404 = action not found (or IDOR → we're not the owner).
    // 501 = endpoint not yet deployed on this environment.
    if (status === 404 || status === 501) return null;
    throw err;
  }
}

/**
 * Stable dedup key for a pending trade action. Callers MUST use this
 * (not `id`) when comparing poll snapshots — see §6 of the integration
 * guide. Server-populated since c55be5b (live in prod); the local
 * fallback only exists so tests / stubs without the field still work.
 */
export function tradeLogicalKey(
  trade: Pick<PendingTradeAction, 'rule_id' | 'matched_public_key'> & { logical_key?: string },
): string {
  return trade.logical_key || `${trade.rule_id}_${trade.matched_public_key}`;
}

/** Human-readable label for a stored `cancel.reason`. */
export function cancelReasonLabel(reason: CancelReason | null | undefined): string | null {
  switch (reason) {
    case 'user_declined':          return 'User declined';
    case 'expired':                return 'Expired';
    case 'insufficient_balance':   return 'Insufficient balance';
    case 'price_feed_unavailable': return 'Price feed unavailable';
    case 'other':                  return 'Other';
    default:                       return null;
  }
}
